import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Button,
  DialogFooter,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Textarea,
} from '@accure/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { z } from 'zod';

import { DatePicker } from '@/components/form/inputs/date-picker';
import { WorkstreamSchema } from '@/project-management/entities/workstream';
import { AddWorkstreamMutation } from '@/project-management/mutations/add-workstream.mutation';

export type AddWorkstreamCustomFormProps = {
  onClose: () => void;
};

const CustomFormSchema = WorkstreamSchema.pick({
  name: true,
  description: true,
  startDate: true,
  endDate: true,
}).refine(
  (data) => {
    if (!data.startDate || !data.endDate) return true;
    return data.startDate <= data.endDate;
  },
  {
    message: 'End date must be after start date',
    path: ['endDate'],
  },
);
type CustomForm = z.infer<typeof CustomFormSchema>;

export function AddWorkstreamCustomForm({
  onClose,
}: AddWorkstreamCustomFormProps) {
  const { siteId } = useParams<{ siteId: string }>();
  const addWorkstream = AddWorkstreamMutation.useMutation();
  const form = useForm<CustomForm>({
    resolver: zodResolver(CustomFormSchema),
  });

  async function handleSubmit(data: CustomForm) {
    await toast.promise(addWorkstream.mutateAsync({ siteId, custom: data }), {
      pending: 'Creating workstream...',
      success: 'Workstream created successfully.',
      error: 'Failed to create workstream. Please try again.',
    });
    onClose();
  }

  return (
    <>
      <Form {...form}>
        <form
          id="add-workstream-custom-form"
          onSubmit={form.handleSubmit(handleSubmit)}
          className="space-y-4"
        >
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input
                    placeholder="e.g., Site Preparation, Installation, Testing"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Describe the specific activities, deliverables, and key milestones for this workstream..."
                    {...field}
                    rows={3}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid items-start gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="startDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Start Date</FormLabel>
                  <FormControl>
                    <DatePicker value={field.value} onSelect={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="endDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>End Date</FormLabel>
                  <FormControl>
                    <DatePicker value={field.value} onSelect={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </form>
      </Form>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="add-workstream-custom-form"
          disabled={addWorkstream.isLoading}
        >
          {addWorkstream.isLoading ? (
            <>
              <Loader2 className="size-3.5 animate-spin" />
              Creating...
            </>
          ) : (
            <>Create Workstream</>
          )}
        </Button>
      </DialogFooter>
    </>
  );
}
